import {
  Row,
  Col,
  Card,
  CardBody,
  CardTitle,
  CardSubtitle,
  Button,
  Input,
  Label,
  Progress,
  FormGroup,
  Badge,
  Alert,
  Spinner,
} from "reactstrap";
import {
  scoreSimilarityAPI,
  statusBehaviorAPI,
  startBehaviorAPI,
  stopBehaviorAPI,
} from "../../service/BehaviorAnalyticsService";
import { startEC2API, stopEC2API } from "../../service/ModelTestingService";
import RefreshIcon from "@mui/icons-material/Refresh";
import React, { useState, useEffect, useRef } from "react";
import { apiClient } from "../../service/ApiClient";
import TreeNode from "./TreeNode";

const authService = require("../../service/AuthService");

const PANEL_HEIGHT = 480;
// --- deterministic local similarity (fallback only) ---
const tokenize = (s = "") => (s.toLowerCase().match(/[a-z0-9]+/g) || []).filter(Boolean);
const bowVector = (s = "") => {
  const v = Object.create(null);
  for (const t of tokenize(s)) v[t] = (v[t] || 0) + 1;
  return v;
};
const cosineBOW = (a = "", b = "") => {
  const va = bowVector(a), vb = bowVector(b);
  const keys = new Set([...Object.keys(va), ...Object.keys(vb)]);
  let dot = 0, na = 0, nb = 0;
  for (const k of keys) {
    const x = va[k] || 0, y = vb[k] || 0;
    dot += x * y; na += x * x; nb += y * y;
  }
  const denom = Math.sqrt(na) * Math.sqrt(nb) || 1e-12;
  return dot / denom; // [0,1]
};

// ===== Helpers for preview + boxes (same behavior as DataManagement) =====
function computeContainFit(Cw, Ch, Iw, Ih) {
  if (!Cw || !Ch || !Iw || !Ih) return { left: 0, top: 0, w: 0, h: 0, scale: 1 };
  const scale = Math.min(Cw / Iw, Ch / Ih);
  const w = Iw * scale;
  const h = Ih * scale;
  const left = (Cw - w) / 2;
  const top = (Ch - h) / 2;
  return { left, top, w, h, scale };
}

// Normalize VOC pixel box -> [0..1] using natural image size
function pxToNormBox({ xmin, ymin, xmax, ymax }, nat) {
  const w = Math.max(nat.w, 1);
  const h = Math.max(nat.h, 1);
  return {
    xMin: Math.max(0, Math.min(1, xmin / w)),
    yMin: Math.max(0, Math.min(1, ymin / h)),
    xMax: Math.max(0, Math.min(1, xmax / w)),
    yMax: Math.max(0, Math.min(1, ymax / h)),
  };
}

// Very basic Pascal VOC XML parser
function parseVocXml(xmlText) {
  const parser = new DOMParser();
  const xml = parser.parseFromString(xmlText, "application/xml");
  const objs = Array.from(xml.getElementsByTagName("object"));
  return objs.map((o) => {
    const name = (o.getElementsByTagName("name")[0]?.textContent || "").trim();
    const bb = o.getElementsByTagName("bndbox")[0];
    const getNum = (tag) => parseFloat(bb.getElementsByTagName(tag)[0]?.textContent || "0");
    return {
      name,
      xmin: getNum("xmin"),
      ymin: getNum("ymin"),
      xmax: getNum("xmax"),
      ymax: getNum("ymax"),
    };
  });
}

// Derive an XML key next to the image (same folder, .xml)
//   e.g. .../frame_000123.jpg -> .../frame_000123.xml
function deriveXmlRelativePath(imageRelativePath) {
  const parts = imageRelativePath.split("/");
  const file = parts.pop() || "";
  const base = file.replace(/\.[^.]+$/, ""); // strip last extension
  const xmlName = `${base}.xml`;
  return [...parts, xmlName].join("/");
}

const BehaviorAnalytics = ({ instanceId: instanceIdProp }) => {
  const [mode, setMode] = useState("audio");
  const [topic, setTopic] = useState("");
  const [text, setText] = useState("");
  const [threshold, setThreshold] = useState(0.6);
  const [score, setScore] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [usedFallback, setUsedFallback] = useState(false);

  // Script/EC2 status states
  const [statusLoading, setStatusLoading] = useState(false);
  const [statusError, setStatusError] = useState("");
  const [ec2Status, setEc2Status] = useState("");
  const [scriptStatus, setScriptStatus] = useState("");
  const [scriptReady, setScriptReady] = useState(false);
  const [lastChecked, setLastChecked] = useState("");
  const [startingEc2, setStartingEc2] = useState(false);
  const [startingScript, setStartingScript] = useState(false);
  const [refreshPressed, setRefreshPressed] = useState(false);

  // ======= VIDEO MODE state (reuse DataManagement patterns) =======
  const [datasetList, setDatasetList] = useState([]);
  const [selectedDataset, setSelectedDataset] = useState("");
  const [prevDataset, setPrevDataset] = useState(null);

  const [folderTree, setFolderTree] = useState({});
  const [isRefreshing, setIsRefreshing] = useState(false);

  const [collapsedFolders, setCollapsedFolders] = useState({});
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [selectedFolders, setSelectedFolders] = useState([]);
  const [folderMetadata, setFolderMetadata] = useState({});

  const [previewContent, setPreviewContent] = useState(""); // image URL
  const [fileKey, setFileKey] = useState("");               // relative path key
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [fileTag, setFileTag] = useState([]);

  // NEW: natural size + normalized boxes from XML
  const [naturalSize, setNaturalSize] = useState({ w: 0, h: 0 });
  const [normBoxes, setNormBoxes] = useState([]); // [{xMin,yMin,xMax,yMax,label}]
  const [fitBox, setFitBox] = useState({ left: 0, top: 0, w: 0, h: 0, scale: 1 });

  const fileNamesArrayRef = useRef([]); // flattened list for Prev/Next
  const imgRef = useRef(null);
  const previewContainerRef = useRef(null);
  const [isImageReady, setIsImageReady] = useState(false);

  // org/username like DataManagement
  const user = authService.getUser();
  const organization = (user?.organization || "").toLowerCase();
  const username = (user?.name || "").toLowerCase();

  const instanceId = instanceIdProp || "i-0e5494d7c4d042b3d";

  useEffect(() => {
    if (mode === "video") {
      fetchVideoDatasets();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, organization]);

  // ---------- Actions ----------
  const handleCompute = async () => {
    setErrorMsg("");
    setUsedFallback(false);

    const t = topic.trim();
    const x = text.trim();

    if (!t || !x) {
      setScore(null);
      setErrorMsg("Please enter both Topic and Text.");
      return;
    }
    if (!instanceId) {
      setScore(null);
      setErrorMsg("EC2 instance id is missing. Pass it as a prop or set REACT_APP_EC2_INSTANCE_ID.");
      return;
    }

    try {
      setLoading(true);
      const res = await scoreSimilarityAPI(instanceId, t, x);
      if (typeof res?.score === "number") {
        setScore(Number(res.score.toFixed(3)));
      } else if (String(res?.message || "").toLowerCase() === "starting") {
        setScore(null);
        setErrorMsg("Behavior service is starting on EC2. Please try again shortly.");
      } else {
        const s = Math.max(0, Math.min(1, cosineBOW(t, x)));
        setScore(Number(s.toFixed(3)));
        setUsedFallback(true);
      }
    } catch (e) {
      console.error(e);
      const s = Math.max(0, Math.min(1, cosineBOW(t, x)));
      setScore(Number(s.toFixed(3)));
      setUsedFallback(true);
      setErrorMsg("Service unreachable. Showing local fallback score.");
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setTopic("");
    setText("");
    setScore(null);
    setErrorMsg("");
    setUsedFallback(false);
  };

  const handleModeChange = (val) => {
    setMode(val);
    setScore(null);
    setErrorMsg("");
    setUsedFallback(false);
  };

  // Refresh = fetch EC2 + script status
  const handleCheckStatus = async () => {
    setStatusError("");
    if (!instanceId) {
      setStatusError("Missing EC2 instance id.");
      return;
    }
    try {
      setStatusLoading(true);
      const res = await statusBehaviorAPI(instanceId);
      setEc2Status(res.ec2);
      setScriptStatus(res.script);
      setScriptReady(!!res.ready);
      setLastChecked(new Date().toLocaleTimeString());
    } catch (e) {
      console.error(e);
      setStatusError("Failed to fetch status.");
    } finally {
      setStatusLoading(false);
    }
  };

  // EC2 start/stop
  const handleToggleEc2 = async () => {
    if (!instanceId) {
      setStatusError("Missing EC2 instance id.");
      return;
    }
    setStatusError("");
    const isRunning = String(ec2Status || "").toLowerCase() === "running";

    try {
      setStartingEc2(true);
      if (isRunning) {
        await stopEC2API(instanceId);
      } else {
        await startEC2API(instanceId, "BehaviorAnalytics");
      }
      await handleCheckStatus();
    } catch (e) {
      console.error(e);
      setStatusError(isRunning ? "Failed to stop EC2." : "Failed to start EC2.");
    } finally {
      setStartingEc2(false);
    }
  };

  // Script start/stop
  const handleToggleScript = async () => {
    if (!instanceId) {
      setStatusError("Missing EC2 instance id.");
      return;
    }

    const isScriptRunningReady =
      String(scriptStatus || "").toLowerCase() === "running" && !!scriptReady;

    try {
      setStartingScript(true);
      setStatusError("");

      let res;
      if (isScriptRunningReady) {
        // STOP
        res = await stopBehaviorAPI(instanceId);
        if (res.status !== 200 || String(res.data?.message).toLowerCase() !== "stopped") {
          const msg = res.data?.message || "unknown";
          const status = res.status;
          const attempts = res.data?.attempts;
          setStatusError(
            `Failed to stop Behavior script. HTTP ${status}. ${msg}${
              attempts ? ` | Attempts: ${JSON.stringify(attempts)}` : ""
            }`
          );
        }
      } else {
        // START
        res = await startBehaviorAPI(instanceId);
        if (res.status >= 400 || String(res.data?.message).toLowerCase() === "error") {
          const msg = res.data?.message || "unknown";
          setStatusError(`Failed to start Behavior script. ${msg}`);
        }
      }

      await handleCheckStatus();
    } catch (e) {
      console.error(e);
      setStatusError(
        isScriptRunningReady
          ? `Failed to stop Behavior script. ${e?.message || ""}`
          : `Failed to start Behavior script. ${e?.message || ""}`
      );
    } finally {
      setStartingScript(false);
    }
  };

  // === Datasets for dropdown (EXACTLY like DataManagement) ===
  const fetchVideoDatasets = async () => {
    try {
      const response = await apiClient.get("datamanagement", {
        params: { organization },
      });

      const rawMetadata = response.data.folderMetadata || [];
      const onlyClassroom = rawMetadata.filter(
        (item) => String(item.folderName || "").toLowerCase().startsWith("classroom")
      );
      const datasets = onlyClassroom.map((item) => ({
        folderName: item.folderName,
        updatedttm: item.updatedttm,
        file_count: item.file_count,
      }));

      const fm = {};
      rawMetadata.forEach((item) => {
        fm[item.folderName] = {
          updatedttm: item.updatedttm,
          file_count: item.file_count,
        };
      });

      setFolderMetadata(fm);
      setDatasetList(datasets);
    } catch (e) {
      console.error("Failed to fetch dataset list", e);
      setDatasetList([]);
      setFolderMetadata({});
    }
  };

  // Convert flat list -> nested tree (same idea as DataManagement)
  const buildFolderTree = (filePaths) => {
    const tree = {};
    filePaths.forEach((p) => {
      let path = p;
      if (path.endsWith("/")) path = path.slice(0, -1);
      const parts = path.split("/");
      // parts = [org, data, dataset, ...]
      const relativeParts = parts.slice(2); // keep dataset at root like DM
      let cur = tree;
      relativeParts.forEach((part, idx) => {
        if (!cur[part]) cur[part] = idx === relativeParts.length - 1 ? null : {};
        cur = cur[part] ?? {};
      });
    });
    return tree;
  };

  // Fetch files for selected dataset (same endpoint/params as DM)
  const fetchVideoFiles = async (dataset, contToken = null) => {
    if (!dataset) return;
    try {
      setIsRefreshing(true);

      const response = await apiClient.get("datamanagement", {
        params: {
          organization,
          dataset,
          datasetFolderMap: contToken || undefined,
        },
      });

      const fileNamesArray = response.data.flatMap((item) => item.files.map((f) => `${f}`));

      // Maintain a flattened array of relative keys for Prev/Next
      fileNamesArrayRef.current = fileNamesArray.map((full) =>
        full.split("/").slice(2).join("/")
      );

      if (dataset !== prevDataset) {
        setFolderTree({});
        setPrevDataset(dataset);
      }

      const tree = buildFolderTree(fileNamesArray);
      if (tree) setFolderTree((prev) => ({ ...prev, ...tree }));
    } catch (e) {
      console.error("Error fetching files", e);
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleVideoDatasetChange = (e) => {
    const ds = e.target.value;
    setSelectedDataset(ds);
    setPreviewContent("");
    setFileKey("");
    setFileTag([]);
    setNormBoxes([]);
    setNaturalSize({ w: 0, h: 0 });
    fetchVideoFiles(ds);
  };

  // Presigned URL for preview (reuse DM route/shape)
  const generatePresignedUrls = async ({ files, datasetName }) => {
    const payload = {
      action: "generatePresignedUrls",
      organization,
      datasetName,
      username,
      operation: "preview",
      files: files.map((n) => ({ name: n, type: "application/octet-stream" })),
    };
    const { data } = await apiClient.post("datamanagement", payload);
    return data;
  };

  // Selection helpers (mirror DataManagement patterns, simplified)
  const toggleSelection = (filePath) => {
    setSelectedFiles((prev) =>
      prev.includes(filePath) ? prev.filter((f) => f !== filePath) : [...prev, filePath]
    );
  };

  const getAllFilesUnderNode = (node, basePath = "") => {
    let files = [];
    Object.entries(node).forEach(([name, child]) => {
      const full = basePath ? `${basePath}/${name}` : name;
      if (child === null) files.push(full);
      else files = files.concat(getAllFilesUnderNode(child, full));
    });
    return files;
  };

  const toggleFolderSelection = (folderPath, node) => {
    const allFiles = getAllFilesUnderNode(node, folderPath);
    const allSelected = allFiles.every((f) => selectedFiles.includes(f));
    if (allSelected) {
      setSelectedFiles((prev) => prev.filter((f) => !allFiles.includes(f)));
      setSelectedFolders((prev) => prev.filter((p) => p !== folderPath));
    } else {
      setSelectedFiles((prev) => [...prev, ...allFiles.filter((f) => !prev.includes(f))]);
      setSelectedFolders((prev) => [...prev, folderPath]);
    }
  };

  const toggleFolderCollapse = (folderPath) => {
    setCollapsedFolders((prev) => ({ ...prev, [folderPath]: !prev[folderPath] }));
  };

  // ========= Preview + XML boxes =========
  const handleSelectFile = async (relativePath) => {
    setFileKey(relativePath);
    setLoadingPreview(true);
    setIsImageReady(false);
    setPreviewContent("");
    setFileTag([]);
    setNormBoxes([]);
    setNaturalSize({ w: 0, h: 0 });

    try {
      // relativePath starts with dataset (tree root) — DM strips first segment
      const cleanedImage = relativePath.split("/").slice(1).join("/");
      const xmlRelative = deriveXmlRelativePath(cleanedImage);

      // Request presigned URLs for image (and XML in parallel)
      const [imgPresigned, xmlPresigned] = await Promise.all([
        generatePresignedUrls({ files: [cleanedImage], datasetName: selectedDataset }),
        generatePresignedUrls({ files: [xmlRelative], datasetName: selectedDataset }).catch(() => []),
      ]);

      const imgOk = Array.isArray(imgPresigned) && imgPresigned.length > 0;
      const xmlOk = Array.isArray(xmlPresigned) && xmlPresigned.length > 0;

      if (imgOk) {
        setPreviewContent(imgPresigned[0].url);
        setFileTag(imgPresigned[0].tags || []);
      } else {
        setPreviewContent("");
      }

      // If XML available, fetch + parse; we'll normalize AFTER we know natural image size
      if (xmlOk) {
        const xmlUrl = xmlPresigned[0].url;
        const xmlText = await fetch(xmlUrl).then((r) => (r.ok ? r.text() : ""));
        const rawBoxes = xmlText ? parseVocXml(xmlText) : [];
        // Temporarily stash raw pixel boxes on state (normalize after image onLoad)
        // We'll store as a ref to avoid extra re-renders
        tempRawBoxesRef.current = rawBoxes;
      } else {
        tempRawBoxesRef.current = [];
      }
    } catch (e) {
      console.error("Preview/XML error", e);
      setPreviewContent("");
      tempRawBoxesRef.current = [];
    } finally {
      setLoadingPreview(false);
    }
  };

  // Hold raw pixel boxes until we know natural image size
  const tempRawBoxesRef = useRef([]);

  // When the image finishes loading, compute fit + normalize any pending boxes
  const onImageLoad = (e) => {
    const natW = e.currentTarget.naturalWidth || 0;
    const natH = e.currentTarget.naturalHeight || 0;
    setNaturalSize({ w: natW, h: natH });
    setIsImageReady(true);

    // Compute fit for the current container
    const Cw = previewContainerRef.current?.clientWidth || 0;
    const Ch = previewContainerRef.current?.clientHeight || 0;
    setFitBox(computeContainFit(Cw, Ch, natW, natH));

    // Normalize raw pixel boxes (if any)
    if (tempRawBoxesRef.current?.length) {
      const normalized = tempRawBoxesRef.current.map((b) => ({
        ...pxToNormBox({ xmin: b.xmin, ymin: b.ymin, xmax: b.xmax, ymax: b.ymax }, { w: natW, h: natH }),
        label: b.name || "",
      }));
      setNormBoxes(normalized);
    } else {
      setNormBoxes([]);
    }
  };

  // Keep fit in sync if container resizes (optional; simple observer)
  useEffect(() => {
    const el = previewContainerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      if (!naturalSize.w || !naturalSize.h) return;
      const Cw = el.clientWidth || 0;
      const Ch = el.clientHeight || 0;
      setFitBox(computeContainFit(Cw, Ch, naturalSize.w, naturalSize.h));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [naturalSize.w, naturalSize.h]);

  const getAdjacentFile = (list, current, dir) => {
    const idx = list.indexOf(current);
    if (idx < 0) return null;
    if (dir === "previous" && idx > 0) return list[idx - 1];
    if (dir === "next" && idx < list.length - 1) return list[idx + 1];
    return null;
  };

  // ---------- UI helpers ----------
  const computeDisabled =
    loading || !instanceId || (mode === "audio" && (!topic.trim() || !text.trim()));

  const statusBadge = (value) => {
    const v = String(value || "").toLowerCase();
    let color = "secondary";
    if (v === "running") color = "success";
    else if (v === "starting") color = "warning";
    else if (v === "stopped") color = "danger";
    return <Badge color={color} pill>{value || "unknown"}</Badge>;
  };

  const equalCardStyle = { minHeight: 180, padding: "10px 12px" };

  const ec2IsRunning = String(ec2Status || "").toLowerCase() === "running";
  const ec2BtnLabel = ec2IsRunning ? "Stop EC2" : "Start EC2";
  const ec2BtnColor = ec2IsRunning ? "danger" : "secondary";

  const isScriptRunningReady =
    String(scriptStatus || "").toLowerCase() === "running" && !!scriptReady;
  const scriptBtnLabel = isScriptRunningReady ? "Stop Script" : "Start Script";
  const scriptBtnColor = isScriptRunningReady ? "danger" : "primary";

  return (
    <div className="container mt-2">
      <Row className="mb-2">
        <Col xs="12">
          <h5 className="font-md-lg font-bold text-center"
          style={{ marginTop:"70px", fontSize:"20px", marginBottom:"20px" }}
          >Behavior Analytics</h5>
          <p className="text-center text-muted">Analyze video and text from audio data for classroom engagement level detection.</p>
        </Col>
      </Row>

      <Row className="mb-1 g-1">
        <Col md="6" lg="6">
          <Card body color="light" style={equalCardStyle}>
            <CardTitle className="fw-bold text-center" style={{ fontSize: "20px", marginBottom: "40px" }}>
              Mode
            </CardTitle>
            <FormGroup className="mb-0 d-flex align-items-center justify-content-center">
              <Label className="me-2" htmlFor="mode-select">
                <strong>Select input type</strong>
              </Label>
              <Input
                id="mode-select"
                type="select"
                value={mode}
                onChange={(e) => handleModeChange(e.target.value)}
                style={{ maxWidth: 260 }}
              >
                <option value="audio">Text (From Audio)</option>
                <option value="video">Video</option>
              </Input>
            </FormGroup>
          </Card>
        </Col>
      </Row>

      {mode === "audio" && (
        <Row>
          <Col md="6" lg="6">
            <Card body color="light" style={equalCardStyle} className="position-relative">
              <RefreshIcon
                onClick={handleCheckStatus}
                disabled={statusLoading || !instanceId}
                title="Refresh status"
                aria-label="Refresh status"
                className="position-absolute"
                onMouseDown={() => setRefreshPressed(true)}
                onMouseUp={() => setRefreshPressed(false)}
                onMouseLeave={() => setRefreshPressed(false)}
                style={{
                  top: "20px",
                  right: "20px",
                  borderRadius: "50%",
                  width: "25px",
                  height: "25px",
                  padding: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "#ffffff",
                  border: "1px solid #d0d5dd",
                  boxShadow: statusLoading
                    ? "inset 0 2px 0 rgba(0,0,0,0.15)"
                    : refreshPressed
                    ? "inset 0 3px 0 rgba(0,0,0,0.25)"
                    : "0 4px 0 rgba(0,0,0,0.2)",
                  transform: refreshPressed ? "translateY(2px)" : "translateY(0)",
                  transition: "transform 90ms ease, box-shadow 90ms ease",
                  cursor: statusLoading || !instanceId ? "not-allowed" : "pointer",
                }}
              />

              <CardTitle className="fw-bold text-center" style={{ fontSize: "20px", marginBottom: "30px" }}>
                Service Manager
              </CardTitle>

              {statusError && <Alert color="danger" className="mb-2">{statusError}</Alert>}

              <div className="d-flex justify-content-center align-items-center gap-5 mb-3" style={{ marginBottom: "14px" }}>
                <Button
                  size="sm"
                  color={ec2BtnColor}
                  onClick={handleToggleEc2}
                  disabled={startingEc2}
                  style={{
                    boxShadow: "0 3px 0 rgba(0,0,0,0.15)",
                    transform: startingEc2 ? "translateY(1px)" : "translateY(0)",
                    transition: "transform 90ms ease",
                  }}
                >
                  {startingEc2 ? (ec2IsRunning ? "Stopping…" : "Starting…") : ec2BtnLabel}
                </Button>

                <Button
                  size="sm"
                  color={scriptBtnColor}
                  onClick={handleToggleScript}
                  disabled={startingScript}
                  style={{
                    boxShadow: "0 3px 0 rgba(0,0,0,0.15)",
                    transform: startingScript ? "translateY(1px)" : "translateY(0)",
                    transition: "transform 90ms ease",
                  }}
                >
                  {startingScript ? (isScriptRunningReady ? "Stopping Script…" : "Starting Script…") : scriptBtnLabel}
                </Button>
              </div>

              <div className="d-flex justify-content-center align-items-center gap-2 mb-3" style={{ marginBottom: "12px" }}>
                {statusLoading ? (
                  <span className="text-muted" style={{ fontSize: 12 }}>Fetching latest status…</span>
                ) : (
                  lastChecked && <span className="text-muted" style={{ fontSize: 12 }}>Last checked: {lastChecked}</span>
                )}
              </div>

              <div className="d-flex justify-content-center align-items-center gap-5">
                <div>
                  <strong>EC2:</strong> {statusBadge(ec2Status)}
                </div>
                <div>
                  <strong>Script:</strong> {statusBadge(scriptStatus)}{" "}
                  {String(scriptStatus).toLowerCase() === "running" && (
                    <Badge color={scriptReady ? "success" : "warning"} pill className="ms-1">
                      {scriptReady ? "ready" : "warming"}
                    </Badge>
                  )}
                </div>
              </div>
            </Card>
          </Col>
        </Row>
      )}

      <Row className="g-4">
        {mode === "audio" ? (
          <Col md="8" lg="8">
            <Card body color="light">
              <CardTitle tag="h5" className="fw-bold">Topic & Text Input</CardTitle>
              <CardSubtitle className="mb-3 text-muted">Enter both fields to compute similarity.</CardSubtitle>

              {!instanceId && (
                <Alert color="warning" className="mb-3">
                  Missing EC2 instance id. Set <b>REACT_APP_EC2_INSTANCE_ID</b> or pass <b>instanceId</b> prop.
                </Alert>
              )}

              {errorMsg && <Alert color="danger">{errorMsg}</Alert>}

              <FormGroup className="mb-3">
                <Label htmlFor="topic">Topic / Context</Label>
                <Input
                  id="topic"
                  type="textarea"
                  rows="1"
                  value={topic}
                  onChange={(e) => setTopic(e.target.value)}
                  placeholder="e.g., Backpropagation and gradient descent"
                />
              </FormGroup>

              <FormGroup className="mb-3">
                <Label htmlFor="text">Spoken Text / Utterance</Label>
                <Input
                  id="text"
                  type="textarea"
                  rows="5"
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder="e.g., Today we'll derive the gradient update rule..."
                />
              </FormGroup>

              <FormGroup className="mb-3">
                <Label>On-topic threshold: {threshold.toFixed(2)}</Label>
                <Input
                  type="range"
                  min="0"
                  max="1"
                  step="0.01"
                  value={threshold}
                  onChange={(e) => setThreshold(parseFloat(e.target.value))}
                />
              </FormGroup>

              <div className="d-flex gap-2 mb-3 justify-content-center">
                <Button color="primary" onClick={handleCompute} disabled={computeDisabled}>
                  {loading ? "Computing…" : "Compute Similarity"}
                </Button>
                <Button color="secondary" outline onClick={handleClear} disabled={loading}>
                  Clear
                </Button>
              </div>

              <div className="d-flex justify-content-center mt-3">
                <Col md="8" lg="6">
                  <Card body color="light-success" className="text-center shadow-sm">
                    <CardTitle tag="h5" className="fw-bold">Result</CardTitle>
                    <CardSubtitle className="mb-3 text-muted">
                      {usedFallback ? "Local fallback (cosine of bag-of-words)" : "Similarity score"}
                    </CardSubtitle>

                    {score === null ? (
                      <Alert color="secondary">
                        {loading ? "Contacting Behavior service…" : "Enter inputs and click Compute Similarity to see results."}
                      </Alert>
                    ) : (
                      <>
                        <h2 className="mb-2">
                          {score}
                          <Badge color={score >= threshold ? "success" : "warning"} pill className="ms-2">
                            {score >= threshold ? "On-topic" : "Possibly off-topic"}
                          </Badge>
                        </h2>
                        <Progress value={score * 100} color={score >= threshold ? "success" : "warning"} className="mt-2" />
                        {usedFallback && (
                          <Alert color="info" className="mt-3 mb-0">
                            Service was unavailable; showed a deterministic local score instead.
                          </Alert>
                        )}
                      </>
                    )}
                  </Card>
                </Col>
              </div>
            </Card>
          </Col>
        ) : (
          <Col md="12" lg="12">
            <Card body color="light" className="text-center">
              <CardTitle tag="h5" 
                className="fw-bold" 
                style={{ marginTop: "10px", fontSize:"20px" }}
                >
                  Video Analysis
              </CardTitle>
              
              <FormGroup
                className="mb-3"
                style={{
                  marginTop: "40px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Label
                  htmlFor="videoDataset"
                  style={{
                    marginBottom: "8px",
                    textAlign: "center"
                  }}
                >
                  Browse one of our Example output datasets
                </Label>

                <Input
                  id="videoDataset"
                  type="select"
                  value={selectedDataset}
                  onChange={handleVideoDatasetChange}
                  style={{
                    maxWidth: "420px",
                    textAlign: "left",
                    marginBottom: "40px"
                  }}
                >
                  <option value="">-- Select --</option>
                  {datasetList.map((d, i) => (
                    <option key={i} value={d.folderName}>
                      {d.folderName} {d.updatedttm ? `- ${d.updatedttm}` : ""}
                    </option>
                  ))}
                </Input>
              </FormGroup>


              {selectedDataset ? (
                <Row style={{ paddingTop: "0px", marginBottom: "20px" }}>
                  {/* File List Section */}
                  <Col sm="6" md="6">
                    <Card style={{ border: "1px solid #ddd", height: PANEL_HEIGHT, minWidth: "400px" }}>
                      <CardBody
                        style={{
                          fontSize: "13px",
                          height: "450px",
                          overflowY: "auto",
                          border: "1px solid #ddd",
                          borderRadius: "6px",
                          backgroundColor: "#f9f9f9",
                        }}
                      >
                        {isRefreshing && <div><strong>Refreshing file list...</strong></div>}

                        {!isRefreshing && Object.keys(folderTree).map((key) => (
                          <TreeNode
                            key={key}
                            name={key}
                            node={folderTree[key]}
                            path={key}
                            handleSelectFile={handleSelectFile}
                            handleSelectFolder={toggleFolderSelection}
                            handleSelectItem={toggleSelection}
                            handleToggleCollapse={toggleFolderCollapse}
                            collapsedFolders={collapsedFolders}
                            selectedFiles={selectedFiles}
                            selectedFolders={selectedFolders}
                            folderMetadata={folderMetadata}
                            fileKey={fileKey}
                            focusedIndex={-1}
                          />
                        ))}
                      </CardBody>
                    </Card>
                  </Col>

                  {/* File Preview Section */}
                  <Col sm="6" md="6">
                    <Card style={{ border: "1px solid #ddd", height: PANEL_HEIGHT , minWidth: "400px" }}>
                      <CardBody 
                      style={{
                        minHeight: "400px",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "flex-start",
                        textAlign: "center",
                      }}>
                        <h6 style={{ fontWeight:"bold" }}>File Preview</h6>

                        <div
                          ref={previewContainerRef}
                          style={{
                            position: "relative",
                            width: "400px",
                            height: "250px",
                            overflow: "hidden",
                            border: "1px solid #ccc",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor: "#fff",
                          }}
                        >
                          {previewContent?.startsWith("http") && (
                            <img
                              key={previewContent}
                              ref={imgRef}
                              src={previewContent}
                              alt="Preview"
                              decoding="async"
                              loading="eager"
                              fetchPriority="high"
                              draggable={false}
                              onLoad={onImageLoad}
                              onError={() => {
                                setIsImageReady(false);
                                setLoadingPreview(false);
                              }}
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "contain",
                                pointerEvents: isImageReady ? "auto" : "none",
                                visibility: isImageReady ? "visible" : "hidden",
                              }}
                            />
                          )}

                          {/* Spinner overlay */}
                          {loadingPreview && (
                            <div
                              style={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                height: "100%",
                                width: "100%",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                backgroundColor: "rgba(255, 255, 255, 0.6)",
                                zIndex: 100,
                              }}
                            >
                              <Spinner color="primary" />
                            </div>
                          )}

                          {/* Detection overlays from XML (normalized -> fit pixels) */}
                          {isImageReady &&
                            normBoxes.map((rect, idx) => {
                              const x = fitBox.left + rect.xMin * fitBox.w;
                              const y = fitBox.top + rect.yMin * fitBox.h;
                              const w = (rect.xMax - rect.xMin) * fitBox.w;
                              const h = (rect.yMax - rect.yMin) * fitBox.h;
                              return (
                                <div key={`box-${idx}`} style={{ position: "absolute", left: 0, top: 0 }}>
                                  <div
                                    style={{
                                      position: "absolute",
                                      left: x,
                                      top: y,
                                      width: w,
                                      height: h,
                                      border: "2px solid #22c55e",
                                      backgroundColor: "rgba(34,197,94,0.12)",
                                      zIndex: 20,
                                      pointerEvents: "none",
                                    }}
                                  />
                                  <div
                                    style={{
                                      position: "absolute",
                                      left: x,
                                      top: Math.max(y - 18, fitBox.top),
                                      backgroundColor: "rgba(0,0,0,0.65)",
                                      color: "white",
                                      padding: "1px 4px",
                                      fontSize: "10px",
                                      borderRadius: 3,
                                      zIndex: 21,
                                    }}
                                  >
                                    {rect.label || "object"}
                                  </div>
                                </div>
                              );
                            })}
                        </div>

                        <div
                          style={{
                            marginTop: "30px",
                            fontSize: "12px",
                            color: "#666",
                            wordBreak: "break-all",
                            minHeight: "48px",
                          }}
                        >
                          <div style={{ color: "black" }}>
                            <span style={{ fontWeight: "bold", marginRight: "5px" }}>File Name:</span>{" "}
                            {loadingPreview ? "Loading..." : fileKey || "—"}
                            <br />
                          </div>
                          <div style={{ marginTop: "10px", color: "black" }}>
                            <span style={{ fontWeight: "bold", marginRight: "5px" }}>
                              Available Labels:
                            </span>{" "}
                            {loadingPreview
                              ? "Loading..."
                              : Array.isArray(fileTag) && fileTag.length > 0
                              ? fileTag.map((t) => t.name).join(", ")
                              : "No tags found"}
                          </div>
                        </div>

                        <div
                          className="pagination-controls"
                          style={{
                            marginTop: "20px",
                            width: "100%", 
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <Button
                            size="sm"
                            disabled={fileNamesArrayRef.current.indexOf(fileKey) <= 0}
                            onClick={() => {
                              const prevFile = getAdjacentFile(
                                fileNamesArrayRef.current,
                                fileKey,
                                "previous"
                              );
                              if (prevFile) handleSelectFile(prevFile);
                            }}
                            hidden={!previewContent}
                          >
                            Prev
                          </Button>

                          <Button
                            size="sm"
                            disabled={
                              fileNamesArrayRef.current.indexOf(fileKey) >=
                              fileNamesArrayRef.current.length - 1
                            }
                            onClick={() => {
                              const nextFile = getAdjacentFile(
                                fileNamesArrayRef.current,
                                fileKey,
                                "next"
                              );
                              if (nextFile) handleSelectFile(nextFile);
                            }}
                            hidden={!previewContent}
                          >
                            Next
                          </Button>
                        </div>
                      </CardBody>
                    </Card>
                  </Col>
                </Row>
              ) : (
                <Alert color="secondary" className="mt-3 mb-0">
                  Select a dataset to view files and preview.
                </Alert>
              )}
            </Card>
          </Col>
        )}
      </Row>
    </div>
  );
};

export default BehaviorAnalytics;
